import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import { List, PictureAsPdf } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Button,
    ButtonGroup,
    Grid,
    TextField
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { generateSubjectGroupsPDFAPI, getReportSubjectGroups } from 'api/report';
import LoadingPopUp from 'app/components/LoadingPopup';
import { getDateStringYYYYMMDD, getMergedAuthorizedInsts, getMergedAuthorizedUnits } from 'app/utils/appHelpers';
import 'dayjs/locale/tr';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

function ReportFilterCard({
    title,
    setFilterData,
    handleResultList: customHandleResultList,
    generatePDFApi: customGeneratePDFApi,
    showUnitFilter = true,
    additionalFilterContent,
    additionalFilterValues = {}
}) {
    const defaultStartDate = new Date();
    defaultStartDate.setDate(1);
    defaultStartDate.setMonth(0);
    const defaultFinishedDate = new Date();
    const allAuthorizedInsts = useSelector(state => state.auth.allAuthorizedInsts);
    const allReadAuthorizedInsts = useSelector(state => state.auth.allReadAuthorizedInsts);
    const mergedAuthorizedInsts = getMergedAuthorizedInsts(allAuthorizedInsts, allReadAuthorizedInsts);
    const isInstLocked = mergedAuthorizedInsts.length === 1;
    const allAuthorizedUnits = useSelector(state => state.auth.allAuthorizedUnits);
    const allReadAuthorizedUnits = useSelector(state => state.auth.allReadAuthorizedUnits);


    const [state, setState] = useState({
        insts: isInstLocked ? [mergedAuthorizedInsts[0]] : mergedAuthorizedInsts,
        unitId: [],
        startedDate: defaultStartDate,
        finishedDate: defaultFinishedDate,
    });
    const [loading, setLoading] = useState(false);
    const [lastFilter, setLastFilter] = useState(null);

    const [filteredUnits, setFilteredUnits] = useState([]);

    useEffect(() => {
        if (isInstLocked) {
            const onlyInst = mergedAuthorizedInsts[0];
            setState(prevState => (
                prevState.insts.length === 1 && prevState.insts[0] === onlyInst
                    ? prevState
                    : { ...prevState, insts: [onlyInst], unitId: [] }
            ));
        }
    }, [isInstLocked, allAuthorizedInsts, allReadAuthorizedInsts]);

    useEffect(() => {
        const mergedAuthorizedUnits = getMergedAuthorizedUnits(allAuthorizedUnits, allReadAuthorizedUnits);
        if (state.insts && state.insts.length > 0) {
            const newFilteredUnits = mergedAuthorizedUnits.filter(unit =>
                state.insts.includes(unit.inst)
            );
            setFilteredUnits(newFilteredUnits);
        } else if (!isInstLocked) {
            const nextInsts = getMergedAuthorizedInsts(allAuthorizedInsts, allReadAuthorizedInsts);
            setState(prevState => ({ ...prevState, insts: nextInsts }));
            setFilteredUnits(mergedAuthorizedUnits);
        }
    }, [state.insts, allAuthorizedUnits, allReadAuthorizedUnits, allAuthorizedInsts, allReadAuthorizedInsts, isInstLocked]);

    const handleInstChange = (event, newValue) => {
        if (isInstLocked) return;
        if (newValue.length === 0) {
            setState(prevState => ({ ...prevState, insts: mergedAuthorizedInsts, unitId: [] }));
        } else {
            setState(prevState => ({ ...prevState, insts: newValue, unitId: [] }));
        }
    };

    const handleResultList = async () => {
        const newStartDate = getDateStringYYYYMMDD(state.startedDate, "00:00:01");
        const newFinishedDate = getDateStringYYYYMMDD(state.finishedDate, "23:59:59");

        const unitIds = state.unitId.map(unit => unit.id);
        const filters = {
            inst: state.insts.join(','),
            unit: unitIds.length > 0 ? unitIds.join(',') : null,
            startedDate: newStartDate,
            finishedDate: newFinishedDate,
            ...additionalFilterValues
        };

        setLoading(true);
        try {
            if (customHandleResultList) {
                await customHandleResultList(filters);
            } else {
                const res = await getReportSubjectGroups(filters);
                setFilterData(res.data.data);
            }
            setLastFilter(filters);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const generatePDFApi = async () => {
        if (!lastFilter) return;
        setLoading(true);
        try {
            if (customGeneratePDFApi) {
                await customGeneratePDFApi();
            } else {
                await generateSubjectGroupsPDFAPI(lastFilter);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setState({
            insts: isInstLocked ? [mergedAuthorizedInsts[0]] : mergedAuthorizedInsts,
            unitId: [],
            startedDate: defaultStartDate,
            finishedDate: defaultFinishedDate,
        });
        setLastFilter(null);
        setFilterData([]);
    };

    return (
        <>
            <LoadingPopUp open={loading} />
            {!loading && (
                <JumboCardQuick title={title}>
                    <Grid container spacing={1} mb={1}>
                        <Grid item xs={2}>
                            <Autocomplete
                                multiple
                                size="small"
                                id="inst-autocomplete"
                                options={mergedAuthorizedInsts}
                                value={state.insts}
                                onChange={handleInstChange}
                                disabled={isInstLocked}
                                getOptionLabel={(option) => option}
                                renderInput={(params) => (
                                    <TextField {...params} label="Kurum" variant="outlined" size="small" />
                                )}
                            />
                        </Grid>

                        {showUnitFilter && (
                            <Grid item xs={3}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    id="unit-autocomplete"
                                    options={filteredUnits}
                                    value={state.unitId}
                                    onChange={(event, newValue) => {
                                        setState(prevState => ({ ...prevState, unitId: newValue }));
                                    }}
                                    getOptionLabel={(option) => option.name}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Kurul/Kol/Komisyon" variant="outlined" size="small" />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid item xs={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                                <DatePicker
                                    label="Başlangıç"
                                    size="small"
                                    value={state.startedDate}
                                    onChange={(newValue) =>
                                        setState(prevState => ({ ...prevState, startedDate: newValue }))
                                    }
                                    renderInput={(params) => <TextField {...params} size="small" />}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                                <DatePicker
                                    label="Bitiş"
                                    size="small"
                                    value={state.finishedDate}
                                    onChange={(newValue) =>
                                        setState(prevState => ({ ...prevState, finishedDate: newValue }))
                                    }
                                    renderInput={(params) => <TextField {...params} size="small" />}
                                />
                            </LocalizationProvider>
                        </Grid>

                        {additionalFilterContent && (
                            <Grid item xs={2}>
                                {additionalFilterContent}
                            </Grid>
                        )}
                        <Grid item xs={2}>
                            <ButtonGroup>
                                <Button variant="outlined" size="medium" color="success" startIcon={<List />} onClick={handleResultList}>
                                    Listele
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    color="success"
                                    startIcon={<PictureAsPdf />}
                                    disabled={!lastFilter}
                                    onClick={generatePDFApi}
                                >
                                    PDF
                                </Button>
                                <Button variant="outlined" size="medium" color="success" startIcon={<DeleteIcon />} onClick={handleClear}>
                                    TEMİZLE
                                </Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </JumboCardQuick>
            )}
        </>
    );
}

export default ReportFilterCard;
